<template>
  <div>
    <va-modal
      :value="isOpen"
      :okText="$t('reports.templates.buttons.generate')"
      :okClass="'btn btn-primary btn-micro'"
      :okDisabled="isLoading || selectedReportOption === null"
      :okToClose="false"
      @ok="submit"
      :cancelText="$t('reports.templates.buttons.back')"
      :cancelClass="'btn-cancel btn btn-primary btn-micro'"
      @cancel="close"
      size="medium"
    >

      <div slot="header" class="modal-title">
        <span>{{ $t('reports.templates.titles.generate_report_modal') }}</span>
      </div>

      <div class="evollu-form">
        <h5>
          <b>{{ $t('reports.templates.labels.name') }}</b>
        </h5>
        <span>{{ reportTemplate.name }}</span>

        <div class="d-flex justify-content-between mt-4">
          <div>
            <h5 class="mb-2">
              <b>{{ $t('reports.templates.labels.report_type') }}&nbsp;</b>
            </h5>
            <span>{{ $t(`reports.templates.labels.report_types.${reportTypesAdapter[reportTemplate.report_type]}`) }}</span>
          </div>
        </div>
        <div class="d-flex justify-content-between mt-4">
          <div>
            <h5 class="mb-2">
              <b>{{ $t('reports.templates.labels.facilities') }}&nbsp;</b>
            </h5>

            <div v-if="reportTemplate.facilities.length > 0">
              <ShowPartialOrFullList
                :data="reportTemplate.facilities">
                <ul class="row" slot-scope="{ data }">
                  <li
                    class="col-6"
                    v-for="(facility, index) in data"
                    :key="index"
                  >
                    {{ facility.name }}
                  </li>
                </ul>
              </ShowPartialOrFullList>
            </div>
            <div v-else-if="reportTemplate.facility_cluster">
              {{ `${$t('reports.templates.labels.every_facility_of_cluster')} ${reportTemplate.facility_cluster.name}` }}
            </div>
            <div v-else>
              {{ $t('reports.templates.labels.every_facility_of_entity') }}
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12 col-md-8">
            <RangeDatePicker
              id="date-picker-updateDate"
              :label="$t('reports.templates.labels.start_date')"
              v-model="dateInterval"
              :config="datePickerConfig"
              placeholder="---"
              @input="form.errors.clear('start_date')"
              :error="form.errors.has('start_date')"
              :error-messages="[$t(form.errors.get('start_date'))]"
            >
            </RangeDatePicker>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12 col-md-8">
            <label class="form-label">{{$t('reports.templates.labels.client')}}:</label>
            <multiselect
              id="client-multiselect"
              :disabled="clientOptions.length === 1"
              v-model="form.data.client"
              :options="clientOptions"
              track-by="id"
              label="name"
              select-label=""
              :placeholder="$t('reports.templates.labels.multiselect.client_placeholder')"
              deselect-label="×"
              open-direction="bellow"
              @select="form.errors.clear('client')"
              :allow-empty="false"
              :multiple="false">
              <template slot="singleLabel" slot-scope="option">
                {{ option.option.isCluster ? presentCluster(option.option) : presentEntity(option.option)}}
              </template>
              <template slot="option" slot-scope="props">
                {{ props.option.isCluster ? presentCluster(props.option) : presentEntity(props.option) }}
              </template>
            </multiselect>
            <small v-show="form.errors.has('client')"  class="help text-danger">
              {{ $t(form.errors.get('client')) }}
            </small>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12 col-md-8">
            <label class="form-label">{{$t('reports.templates.labels.report_type')}}:</label>
            <multiselect
              id="report-type-multiselect"
              v-model="selectedReportOption"
              :options="reportOptions"
              track-by="id"
              :custom-label="option => $t(`reports.templates.labels.report_types.${option.type}`)"
              :placeholder="$t('reports.templates.labels.multiselect.report_type_placeholder')"
              deselect-label="×"
              open-direction="bellow"
              :allow-empty="false"
              :disabled="reportTemplate.report_type !== 1 || !isFromEvollu"
              :multiple="false">
            </multiselect>
          </div>
        </div>

      </div>
      <div v-show="isLoading" class="text-center">
        <h6><b>{{$t('reports.templates.labels.is_loading')}}</b></h6>
        <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
      </div>
    </va-modal>
  </div>
</template>

<script>

import Multiselect from 'vue-multiselect'
import ShowPartialOrFullList from '@/components/common/ShowPartialOrFullList'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import Errors from '@/utils/StandardErrors.js'
import Vue from 'vue'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'
import { ReportTemplatesService } from '@/api/ReportsService'

export default {
  props: {
    reportTemplate: {
      type: Object,
      required: true
    },
    reportTypesAdapter: {
      type: Object,
      required: true
    },
    serviceUrl: {
      type: String,
      required: true
    },
    writableEntities: {
      type: Array,
      required: true
    }
  },
  components: {
    LoadingAnimation,
    RangeDatePicker,
    ShowPartialOrFullList,
    Multiselect
  },
  mixins: [
    MultiselectLabelFormatter,
    IsFromEvolluMixin
  ],
  mounted: function () {
    this.isOpen = true
    this.form.data.start_date = this.moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD')
    this.form.data.end_date = this.moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
    this.form.data.client = this.clientOptions[0]

    if (this.reportTemplate.report_type !== 1 || !this.isFromEvollu) {
      this.selectedReportOption = this.reportOptions[0]
    }
  },
  data: function () {
    return {
      isLoading: false,
      isOpen: false,
      form: {
        data: {
          start_date: undefined,
          end_date: undefined,
          client: undefined,
        },
        errors: new Errors()
      },
      reportOptions: [
        { id: 1, type: 'monthlyView' },
        { id: 2, type: 'adjustt' }
      ],
      selectedReportOption: null
    }
  },
  computed: {
    dateInterval: function () {
      if (this.form.data.start_date && this.form.data.end_date) {
        return {
          start: this.form.data.start_date,
          end: this.form.data.end_date
        }
      }
      return ''
    },
    datePickerConfig: function () {
      let config = { }
      config.minDate = this.moment('2019-03-01').format('YYYY-MM-DD')

      config.onChange = (selectedDates, dateStr, instance) => {
        if (selectedDates.length === 0) {
          this.form.data.start_date = undefined
          this.form.data.end_date = undefined
        } else if (selectedDates.length === 2) {
          this.form.data.start_date = this.moment(selectedDates[0]).format('YYYY-MM-DD')
          this.form.data.end_date = this.moment(selectedDates[1]).format('YYYY-MM-DD')
        }
      }

      return config
    },
    clientOptions: function () {
      if (this.reportTemplate.entity_cluster) {
        return [{
          id: this.reportTemplate.entity_cluster.id,
          name: this.reportTemplate.entity_cluster.name,
          isCluster: true
        }]
      } else {
        return this.reportTemplate.entities.filter(entity => this.writableEntities.some(writableEntity => entity.id === writableEntity.id))
      }
    }
  },
  methods: {
    getFormData: function () {
      return {
        start_date: this.form.data.start_date,
        end_date: this.form.data.end_date,
        client_id: this.form.data.client.id
      }
    },
    onSubmissionErrors: function (response) {
      if (response.status === 422) {
        this.form.errors.record(response.data, 'reports.templates.errors.')
      } else if (response.status === 403 && response.data.hasOwnProperty('message')) {
        this.showToast(this.$t(`reports.templates.messages.${response.data.message}`), this.$store.state.errorToastOptions)
      } else {
        this.showToast(this.$t('reports.templates.messages.something_went_wrong'), this.$store.state.errorToastOptions)
      }
    },
    presentCluster: function (cluster) {
      return `${cluster.name} 📂`
    },
    presentEntity: function (entity) {
      let name = entity.name
      if (entity.cities.length > 0) {
        name += ` (${entity.cities.join(', ')})`
      }
      return name
    },
    submit: function () {
      // id 1 is the "normal" report type, 2 is the adjustt type
      this.isLoading = true
      if (this.selectedReportOption.id === 1) {
        ReportTemplatesService.generateReports(this.reportTemplate.id, this.getFormData())
          .then(response => {
            this.$emit('report-generated')
            this.isOpen = false
          })
          .catch(errors => this.onSubmissionErrors(errors.response))
          .finally(() => { this.isLoading = false })
      } else {
        ReportTemplatesService.submitAdjusttReport(this.reportTemplate.id, this.getFormData())
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${this.reportTemplate.name}_Adjustt.xlsx`)
            document.body.appendChild(link)
            link.click()
            this.$emit('report-generated')
            this.isOpen = false
          })
          .catch(errors => this.onSubmissionErrors(errors.response))
          .finally(() => { this.isLoading = false })
      }
    },
    close: function () {
      setTimeout(() => {
        this.$emit('modalWasClosed')
      }, 500)
    }
  }
}
</script>
<style scoped lang="scss">

</style>
